import { useEffect, useMemo, useState } from 'react'
import { useBreadcrumb } from '../hooks/useBreadcrumb'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  FaWallet,
  FaArrowUp,
  FaArrowDown,
  FaHistory,
  FaExchangeAlt,
  FaCheckCircle,
  FaExclamationTriangle,
  FaMoneyCheckAlt,
  FaSearch,
  FaFileAlt,
  FaCalculator,
  FaClock,
} from 'react-icons/fa'
import { financeService } from '../services'
import { Card, PageHeader, LoadingOverlay, EmptyState, Button, MobileCard, Modal } from '../components/common'
import { useAuthStore } from '../store'
import { AmountDisplay, GuestPrompt } from '../hooks'
import { formatDateTime } from '../utils/datetime'

const financeTabLabels = {
  overview: '财务管理',
  recharge: '充值',
  financing: '融资服务',
  forex: '收汇服务',
  exchange: '汇率测算',
  reconciliation: '对账',
  transaction: '交易记录',
  payment: '付款',
}

const normalizeFinanceTab = (tab) => {
  const value = (tab || '').trim()
  if (value === 'pay' || value === 'payment-records') return 'payment'
  if (financeTabLabels[value]) return value
  return 'overview'
}

const resolveFinanceEntryLabel = (tab) => {
  const value = (tab || '').trim()
  if (value === 'payment-records') return '付款记录'
  if (value === 'pay') return '付款'
  return financeTabLabels[normalizeFinanceTab(value)] || '财务管理'
}

/**
 * 页内 tab 条。
 *
 * 【来历】这本来就是页内 tab：2026-02-24 的版本(0f5667e)有一排 tab 按钮，
 * 2026-03-02 的 8a85c84 把它删掉、改由导航「财务管理」下拉传 ?tab= 来切。
 * 于是切 tab 必须回到顶部菜单，且页面自己没有任何 tab 界面。2026-08-01 改回页内 tab，
 * 导航那个下拉随之撤销（见 Navbar.jsx）。
 *
 * 【为什么是这七项】按当时下拉里的七项原样搬过来，不是 0f5667e 那份八项旧清单。
 * 两处差异是有意保留的：
 *   - 「账户概览」不在 tab 里 —— 它是不带 ?tab= 时的落地内容，
 *     账号菜单里的「财务管理」链的就是 /portal/finance，点它即回到概览。
 *   - 「汇率测算」(exchange) 的 tab 内容仍在页面里，但下拉当时就没放它，
 *     所以这里也不放，保持与改动前的可达范围完全一致。要放出来加一行即可。
 *
 * 【key 用的是 URL 原值，不是归一化后的值】'pay' 与 'payment-records' 经
 * normalizeFinanceTab 都归到同一个 payment tab（内容本就是同一个），
 * 但高亮要跟着用户点的那个走，所以这里保留原始 key，只有渲染内容时才归一化。
 */
const financeTabs = [
  { key: 'pay', label: '付款' },
  { key: 'recharge', label: '充值' },
  { key: 'reconciliation', label: '对账' },
  { key: 'transaction', label: '交易记录' },
  { key: 'payment-records', label: '付款记录' },
  { key: 'forex', label: '收汇服务' },
  { key: 'financing', label: '融资服务' },
]

const paymentMethods = [
  { value: 'wechat', label: '微信', hint: '支持企业微信转账' },
  { value: 'alipay', label: '支付宝', hint: '支持扫码/转账' },
  { value: 'bank', label: '银行卡', hint: '对公转账，到账需时' },
]

const financingTypes = [
  { value: 'working_capital', label: '周转贷' },
  { value: 'order_financing', label: '订单融资' },
  { value: 'credit', label: '信用贷' },
]

const forexTargets = ['USD', 'EUR', 'HKD', 'JPY', 'GBP']

const financingConditions = ['企业运营满 6 个月', '近 3 个月稳定发货记录', '无重大逾期/司法风险']
const financingMaterials = ['营业执照', '近三个月流水', '贸易合同/发票', '法人身份证明']
const financingCases = [
  { company: '深圳某跨境卖家', amount: '200,000', result: '45 天完成周转，旺季备货及时' },
  { company: '东莞某电子配件商', amount: '500,000', result: '授信后平均物流成本下降 8%' },
]

const financingStatusMap = {
  0: '待审批',
  1: '已审批',
  2: '已放款',
  3: '已拒绝',
  pending: '待审批',
  approved: '已审批',
  disbursed: '已放款',
  rejected: '已拒绝',
}

const reconcileLabelMap = {
  unreconciled: '未对账',
  reconciled: '已对账',
  abnormal: '异常',
}

const typeLabelMap = {
  recharge: '充值',
  consume: '消费',
  payment: '付款',
  deduct: '扣款',
  withdraw: '提现',
}

export default function FinancePage() {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const { customer, isGuest, isAuthenticated } = useAuthStore()
  const isGuestMode = isGuest || !isAuthenticated

  const initialTab = normalizeFinanceTab(searchParams.get('tab'))
  const [activeTab, setActiveTab] = useState(initialTab)
  const [entryLabel, setEntryLabel] = useState(resolveFinanceEntryLabel(searchParams.get('tab')))
  // 七个 tab 是页内切换（?tab=），不是独立路由，所以作为面包屑的最后一层挂上去。
  // 必须放在下方 isLoading 早退之前 —— hook 不能被条件调用
  const breadcrumb = useBreadcrumb(activeTab === 'overview' ? [] : [{ label: entryLabel }])
  // URL 里的 tab 原值，只用于 tab 条高亮（见 financeTabs 注释：pay 与 payment-records
  // 会被归一化成同一个 activeTab，靠它才能区分用户点的是哪一个）
  const [selectedTabKey, setSelectedTabKey] = useState(searchParams.get('tab') || '')
  const [rechargeAmount, setRechargeAmount] = useState('')
  const [rechargeRemark, setRechargeRemark] = useState('')
  const [rechargeMethod, setRechargeMethod] = useState('')

  const [financingType, setFinancingType] = useState('')
  const [financingAmount, setFinancingAmount] = useState('')
  const [financingRemark, setFinancingRemark] = useState('')
  const [financingTerm, setFinancingTerm] = useState('6')
  const [financingContactName, setFinancingContactName] = useState('')
  const [financingContactPhone, setFinancingContactPhone] = useState('')
  const [financingApplyVisible, setFinancingApplyVisible] = useState(false)
  const [financingQueryKeyword, setFinancingQueryKeyword] = useState('')
  const [calculatorPrincipal, setCalculatorPrincipal] = useState('100000')
  const [calculatorRate, setCalculatorRate] = useState('8')
  const [calculatorMonths, setCalculatorMonths] = useState('12')

  const [forexTargetCurrency, setForexTargetCurrency] = useState('USD')
  const [forexAmount, setForexAmount] = useState('')
  const [forexRemark, setForexRemark] = useState('')
  const [forexAuditQueue, setForexAuditQueue] = useState([])
  const [activeForexAuditId, setActiveForexAuditId] = useState('')

  const [sourceCurrency, setSourceCurrency] = useState('CNY')
  const [targetCurrency, setTargetCurrency] = useState('USD')
  const [exchangeAmount, setExchangeAmount] = useState('')
  const [exchangeResult, setExchangeResult] = useState(null)

  const [reconcileStatus, setReconcileStatus] = useState('')
  const [exceptionModal, setExceptionModal] = useState({ visible: false, transactionId: null })
  const [exceptionText, setExceptionText] = useState('')

  const [transactionKeyword, setTransactionKeyword] = useState('')
  const [transactionType, setTransactionType] = useState('')
  const [paymentKeyword, setPaymentKeyword] = useState('')

  useEffect(() => {
    const tab = searchParams.get('tab')
    setActiveTab(normalizeFinanceTab(tab))
    setEntryLabel(resolveFinanceEntryLabel(tab))
    setSelectedTabKey(tab || '')
  }, [searchParams])

  /**
   * 切 tab 只改地址，其余交给上面那个 effect 统一同步 —— URL 是唯一真相，
   * 避免「点击改 state、地址没跟上」两套状态打架。
   *
   * 保持 push（不加 replace）：原先每点一次下拉都会压一条历史，后退能回到上一个 tab，
   * 改成页内 tab 后维持同样的手感。旧的 ?tab= 链接也照常可用。
   */
  const handleTabClick = (key) => {
    setSearchParams({ tab: key })
  }

  // 交易类列表在小屏的卡片形态。本页三张表（最近交易/交易记录/付款记录）字段一致，共用这一份。
  const renderTxnCards = (rows) => (
    <div className="md:hidden space-y-3 p-4">
      {rows.map((item) => (
        <MobileCard
          key={item.id}
          title={<span className="text-base">¥{parseFloat(item.amount || 0).toFixed(2)}</span>}
          subtitle={[item.transactionNo || item.id, formatDateTime(item.createTime, 'YYYY-MM-DD HH:mm')].filter(Boolean).join('  ·  ')}
          badge={<span className="text-xs text-gray-500">{typeLabelMap[item.transactionType] || item.transactionType}</span>}
          fields={[
            { label: '余额', value: `¥${parseFloat(item.balanceAfter || 0).toFixed(2)}` },
            item.description && { label: '说明', value: item.description },
          ]}
        />
      ))}
    </div>
  )

  const { data: accountInfo, isLoading: accountLoading } = useQuery({
    queryKey: ['accountInfo'],
    queryFn: financeService.getAccountInfo,
    enabled: !isGuestMode,
  })

  const { data: transactionPage, isLoading: transactionLoading } = useQuery({
    queryKey: ['finance-transactions', transactionType, transactionKeyword],
    queryFn: () => financeService.queryTransactions({
      current: 1,
      size: 30,
      transactionType: transactionType || undefined,
      keyword: transactionKeyword || undefined,
    }),
    enabled: !isGuestMode,
  })

  const { data: paymentPage, isLoading: paymentLoading } = useQuery({
    queryKey: ['finance-payments', paymentKeyword],
    queryFn: () => financeService.getPaymentRecords({
      current: 1,
      size: 30,
      keyword: paymentKeyword || undefined,
    }),
    enabled: !isGuestMode,
  })

  const { data: reconciliationPage, isLoading: reconciliationLoading } = useQuery({
    queryKey: ['finance-reconciliation', reconcileStatus],
    queryFn: () => financeService.getReconciliationList({
      current: 1,
      size: 30,
      reconcileStatus: reconcileStatus || undefined,
    }),
    enabled: !isGuestMode,
  })

  const { data: exchangeRates } = useQuery({
    queryKey: ['finance-exchange-rates'],
    queryFn: financeService.getExchangeRates,
    enabled: !isGuestMode,
  })

  const { data: financingPage } = useQuery({
    queryKey: ['finance-financing-applications'],
    queryFn: () => financeService.getFinancingApplications({ current: 1, size: 30 }),
    enabled: !isGuestMode,
  })

  const { data: forexPage } = useQuery({
    queryKey: ['finance-forex-applications'],
    queryFn: () => financeService.getForexApplications({ current: 1, size: 30 }),
    enabled: !isGuestMode,
  })

  const rechargeMutation = useMutation({
    mutationFn: ({ amount, remark, paymentMethod }) => financeService.requestRecharge(amount, remark, paymentMethod),
    onSuccess: () => {
      toast.success('充值申请已提交，请按所选方式完成付款')
      setRechargeAmount('')
      setRechargeRemark('')
      setRechargeMethod('')
      queryClient.invalidateQueries({ queryKey: ['accountInfo'] })
      queryClient.invalidateQueries({ queryKey: ['finance-transactions'] })
      queryClient.invalidateQueries({ queryKey: ['finance-reconciliation'] })
    },
    onError: (error) => toast.error(error.message || '充值失败'),
  })

  const financingMutation = useMutation({
    mutationFn: (payload) => financeService.submitFinancingApplication(payload),
    onSuccess: () => {
      toast.success('融资申请已提交')
      setFinancingAmount('')
      setFinancingType('')
      setFinancingRemark('')
      setFinancingTerm('6')
      setFinancingContactName('')
      setFinancingContactPhone('')
      setFinancingApplyVisible(false)
      queryClient.invalidateQueries({ queryKey: ['finance-financing-applications'] })
    },
    onError: (error) => toast.error(error.message || '提交融资申请失败'),
  })

  const forexMutation = useMutation({
    mutationFn: (payload) => financeService.submitForexApplication(payload),
    onSuccess: () => {
      toast.success('收汇申请已提交')
      queryClient.invalidateQueries({ queryKey: ['finance-forex-applications'] })
      setForexAmount('')
      setForexRemark('')
      setForexTargetCurrency('USD')
    },
    onError: (error) => toast.error(error.message || '提交收汇申请失败'),
  })

  const exchangeMutation = useMutation({
    mutationFn: (payload) => financeService.calculateExchange(payload),
    onSuccess: (result) => setExchangeResult(result),
    onError: (error) => toast.error(error.message || '汇率测算失败'),
  })

  const markMutation = useMutation({
    mutationFn: (transactionId) => financeService.markReconciled(transactionId),
    onSuccess: () => {
      toast.success('已标记为已对账')
      queryClient.invalidateQueries({ queryKey: ['finance-reconciliation'] })
    },
    onError: (error) => toast.error(error.message || '操作失败'),
  })

  const exceptionMutation = useMutation({
    mutationFn: ({ transactionId, issueDescription }) =>
      financeService.submitReconciliationException(transactionId, issueDescription),
    onSuccess: () => {
      toast.success('异常已提交，已同步到问题查询与异常警报')
      setExceptionModal({ visible: false, transactionId: null })
      setExceptionText('')
      queryClient.invalidateQueries({ queryKey: ['finance-reconciliation'] })
      queryClient.invalidateQueries({ queryKey: ['issues'] })
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] })
    },
    onError: (error) => toast.error(error.message || '提交异常失败'),
  })

  const transactions = transactionPage?.records || []
  const payments = paymentPage?.records || []
  const reconciliationList = reconciliationPage?.records || []
  // 包 useMemo：不包则每次渲染都是新数组，financingFiltered 的依赖恒变（同 CargoPage）
  const financingRecords = useMemo(() => financingPage?.records || [], [financingPage])
  const forexRecords = useMemo(() => {
    if (Array.isArray(forexPage)) return forexPage
    if (Array.isArray(forexPage?.records)) return forexPage.records
    if (Array.isArray(forexPage?.data?.records)) return forexPage.data.records
    if (Array.isArray(forexPage?.list)) return forexPage.list
    return []
  }, [forexPage])
  const currencyOptions = useMemo(() => Object.keys(exchangeRates || {}), [exchangeRates])
  /*
   * 过滤字段必须跟表格【显示】的那一列对齐：那一列渲染的是 `applicationNo || id`
   * （见下方表格与卡片），而接口目前并不返回 applicationNo / customerName，只有 id。
   * 原先只匹配 applicationNo + customerName，结果用户照着屏幕上的编号搜也是 0 行 ——
   * 搜索框对融资申请等于完全不可用。补上 id 后与所见一致。
   */
  const financingFiltered = useMemo(() => {
    if (!financingQueryKeyword) return financingRecords
    const keyword = financingQueryKeyword.trim().toLowerCase()
    if (!keyword) return financingRecords
    return financingRecords.filter((item) =>
      String(item.applicationNo || item.id || '').toLowerCase().includes(keyword)
      || String(item.customerName || '').toLowerCase().includes(keyword)
    )
  }, [financingRecords, financingQueryKeyword])
  const activeForexAudit = useMemo(
    () => forexAuditQueue.find((item) => item.id === activeForexAuditId) || null,
    [forexAuditQueue, activeForexAuditId]
  )
  const financingCalc = useMemo(() => {
    const principal = parseFloat(calculatorPrincipal)
    const annualRate = parseFloat(calculatorRate)
    const months = parseInt(calculatorMonths, 10)
    if (!principal || !annualRate || !months) return null
    const monthlyRate = annualRate / 12 / 100
    const monthly = monthlyRate === 0
      ? principal / months
      : (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1)
    const totalRepayment = monthly * months
    return {
      monthly,
      totalInterest: totalRepayment - principal,
      totalRepayment,
    }
  }, [calculatorPrincipal, calculatorRate, calculatorMonths])

  useEffect(() => {
    const mapped = forexRecords.map((item) => {
      const statusMap = {
        pending: '待审核',
        approved: '已通过',
        rejected: '已驳回',
        completed: '已完成',
      }
      return {
        id: item.application_code || item.applicationCode || String(item.id),
        backendId: item.id,
        sourceProof: item.income_source_proof || item.incomeSourceProof || item.remark || '待补充',
        tradeBackground: item.trade_background_info || item.tradeBackgroundInfo || item.remark || '待补充交易背景资料',
        applicantQualification: item.applicant_qualification_docs || item.applicantQualificationDocs || item.remark || '待提交申请人资质文件',
        amount: item.amount,
        currency: item.target_currency || item.targetCurrency,
        status: statusMap[item.status] || item.status || '待审核',
        process: [
          { name: '提交申请', time: formatDateTime(item.create_time || item.createTime, 'YYYY-MM-DD HH:mm'), done: true },
          { name: '资料预审', time: item.status === 'pending' ? '-' : formatDateTime(item.update_time || item.updateTime, 'YYYY-MM-DD HH:mm'), done: item.status !== 'pending' },
          { name: '风控审核', time: formatDateTime(item.approval_time || item.approvalTime, 'YYYY-MM-DD HH:mm'), done: item.status !== 'pending' },
          { name: '结果通知', time: item.status === 'pending' ? '-' : formatDateTime(item.update_time || item.updateTime, 'YYYY-MM-DD HH:mm'), done: item.status !== 'pending' },
        ],
      }
    })
    setForexAuditQueue(mapped)
    if (mapped.length === 0) {
      setActiveForexAuditId('')
      return
    }
    if (!mapped.some((item) => item.id === activeForexAuditId)) {
      setActiveForexAuditId(mapped[0].id)
    }
  }, [forexRecords, activeForexAuditId])

  const isLoading = accountLoading || transactionLoading || paymentLoading || reconciliationLoading

  const handleRecharge = () => {
    const amount = parseFloat(rechargeAmount)
    if (!amount || amount <= 0) {
      toast.error('请输入正确的充值金额')
      return
    }
    if (!rechargeMethod) {
      toast.error('请选择支付方式')
      return
    }
    rechargeMutation.mutate({ amount, remark: rechargeRemark, paymentMethod: rechargeMethod })
  }

  const handleFinancingSubmit = () => {
    const amount = parseFloat(financingAmount)
    if (!financingType) {
      toast.error('请选择融资产品')
      return
    }
    if (!amount || amount <= 0) {
      toast.error('请输入融资金额')
      return
    }
    financingMutation.mutate({
      applicationType: financingType,
      requestedAmount: amount,
      term: parseInt(financingTerm, 10) || 6,
      contactName: financingContactName,
      contactPhone: financingContactPhone,
      remark: financingRemark,
    })
  }

  const handleForexSubmit = () => {
    const amount = parseFloat(forexAmount)
    if (!amount || amount <= 0) {
      toast.error('请输入收汇金额')
      return
    }
    forexMutation.mutate({ targetCurrency: forexTargetCurrency, amount, remark: forexRemark })
  }

  const handleCalculateExchange = () => {
    const amount = parseFloat(exchangeAmount)
    if (!amount || amount <= 0) {
      toast.error('请输入正确的金额')
      return
    }
    exchangeMutation.mutate({ sourceCurrency, targetCurrency, amount })
  }

  const canMarkReconciled = (item) => {
    return !(item.reconcileStatus === 'reconciled' && item.reconcileResult === 'normal')
  }

  if (isLoading && !isGuestMode) {
    return <LoadingOverlay message="加载中..." />
  }


  return (
    <div>
      <PageHeader
        icon={FaWallet}
        title="财务管理"
        subtitle="充值、汇率测算、对账管理、交易查询、付款记录"
        breadcrumb={breadcrumb}
      />

      <div className="container py-8">
        <div className="grid grid-cols-fluid-lg gap-6 mb-8">
          <Card className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
            <Card.Body>
              <div className="flex items-center justify-between mb-4">
                <span className="text-white/80">账户余额</span>
                <FaWallet className="text-xl text-white/60" />
              </div>
              {/* 金额位数不定（可能上万），小屏 text-fluid-4xl 会溢出卡片 */}
              <div className="text-fluid-4xl font-bold mb-2 break-all">
                <AmountDisplay
                  amount={accountInfo?.balance || customer?.account_balance || 0}
                  showLoginPrompt={isGuestMode}
                  className="text-white"
                />
              </div>
              <p className="text-white/70 text-sm">账户状态: {isGuestMode ? '访客模式' : '正常'}</p>
            </Card.Body>
          </Card>

          <Card>
            <Card.Body>
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-500">累计充值</span>
                <div className="w-10 h-10 rounded-lg bg-green-100 text-green-600 flex items-center justify-center">
                  <FaArrowDown />
                </div>
              </div>
              <div className="text-fluid-2xl font-bold text-gray-900 break-all">
                <AmountDisplay amount={accountInfo?.totalRecharge || 0} showLoginPrompt={isGuestMode} />
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Body>
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-500">累计消费</span>
                <div className="w-10 h-10 rounded-lg bg-red-100 text-red-600 flex items-center justify-center">
                  <FaArrowUp />
                </div>
              </div>
              <div className="text-fluid-2xl font-bold text-gray-900 break-all">
                <AmountDisplay amount={accountInfo?.totalConsumed || 0} showLoginPrompt={isGuestMode} />
              </div>
            </Card.Body>
          </Card>
        </div>

        {isGuestMode ? (
          <GuestPrompt message="登录后使用完整财务管理功能" className="mb-8" />
        ) : (
          <>
            {/* 页内 tab 条。位置与样式沿用 0f5667e 那版：余额卡片之下、内容之上，
                游客态不显示（上面走的是 GuestPrompt 分支）。 */}
            <Card className="mb-6">
              <Card.Body>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                  {financeTabs.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => handleTabClick(tab.key)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors shadow-md ${
                        selectedTabKey === tab.key
                          ? 'bg-primary-700 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </Card.Body>
            </Card>

            {activeTab === 'overview' && (
              <Card>
                <Card.Header>
                  <h5 className="font-semibold flex items-center gap-2">
                    <FaHistory className="text-primary-500" /> 最近交易
                  </h5>
                </Card.Header>
                <Card.Body className="p-0">
                  {transactions.length > 0 ? (
                    <>
                    {renderTxnCards(transactions.slice(0, 10))}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>交易编号</th>
                            <th>类型</th>
                            <th>金额</th>
                            <th>余额</th>
                            <th>说明</th>
                            <th>时间</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactions.slice(0, 10).map((item) => (
                            <tr key={item.id}>
                              <td>{item.transactionNo || item.id}</td>
                              <td>{typeLabelMap[item.transactionType] || item.transactionType}</td>
                              <td className="font-medium">¥{parseFloat(item.amount || 0).toFixed(2)}</td>
                              <td>¥{parseFloat(item.balanceAfter || 0).toFixed(2)}</td>
                              <td>{item.description || '-'}</td>
                              <td>{formatDateTime(item.createTime, 'YYYY-MM-DD HH:mm')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    </>
                  ) : (
                    <EmptyState title="暂无交易记录" description="当前没有可展示的数据" />
                  )}
                </Card.Body>
              </Card>
            )}

            {activeTab === 'recharge' && (
              <Card>
                <Card.Header>
                  <h5 className="font-semibold flex items-center gap-2">
                    <FaMoneyCheckAlt className="text-primary-500" /> 充值功能
                  </h5>
                </Card.Header>
                <Card.Body>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="md:col-span-2 grid grid-cols-fluid-md gap-4">
                      <div>
                        <label className="form-label">充值金额</label>
                        <input
                          type="number"
                          className="form-control"
                          value={rechargeAmount}
                          onChange={(e) => setRechargeAmount(e.target.value)}
                          placeholder="请输入充值金额"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <div>
                        <label className="form-label">备注</label>
                        <input
                          type="text"
                          className="form-control"
                          value={rechargeRemark}
                          onChange={(e) => setRechargeRemark(e.target.value)}
                          placeholder="例如：订单号/用途"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="form-label">支付方式</label>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          {paymentMethods.map((method) => (
                            <button
                              key={method.value}
                              type="button"
                              disabled
                              className="relative p-3 rounded-lg border text-left transition-all border-gray-200 text-gray-400 bg-gray-50 opacity-80 cursor-not-allowed"
                            >
                              <div className="font-semibold">{method.label}</div>
                              <div className="text-xs text-gray-500">{method.hint}</div>
                              <span className="absolute right-2 bottom-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                                敬请期待
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                      <p className="text-sm text-gray-600 font-medium mb-2">充值说明</p>
                      <ul className="space-y-1 text-xs text-gray-500">
                        <li>• 充值提交后需按所选方式完成付款</li>
                        <li>• 对公转账请备注公司名称与订单号</li>
                        <li>• 财务确认到账后系统自动入账</li>
                      </ul>
                    </div>
                  </div>
                  <div className="mt-4">
                    <Button
                      loading={rechargeMutation.isPending}
                      disabled={!rechargeMethod}
                      onClick={handleRecharge}
                    >
                      提交充值申请
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            )}

            {activeTab === 'financing' && (
              <div className="space-y-6">
                <Card>
                  <Card.Header>
                    <div className="flex items-center justify-between gap-3">
                      <h5 className="font-semibold flex items-center gap-2">
                        <FaMoneyCheckAlt className="text-primary-500" /> 融资服务
                      </h5>
                      <Button onClick={() => setFinancingApplyVisible(true)}>立即申请</Button>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h6 className="font-medium mb-2">申请条件</h6>
                        <ul className="space-y-1 text-sm text-gray-600">
                          {financingConditions.map((item) => <li key={item}>• {item}</li>)}
                        </ul>
                      </div>
                      <div>
                        <h6 className="font-medium mb-2">所需材料</h6>
                        <ul className="space-y-1 text-sm text-gray-600">
                          {financingMaterials.map((item) => <li key={item}>• {item}</li>)}
                        </ul>
                      </div>
                    </div>
                  </Card.Body>
                </Card>

                <Card>
                  <Card.Header>
                    <h5 className="font-semibold flex items-center gap-2"><FaClock className="text-primary-500" /> 申请进度查询</h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="flex flex-col md:flex-row gap-3 mb-4">
                      <input
                        type="text"
                        className="form-control"
                        value={financingQueryKeyword}
                        onChange={(e) => setFinancingQueryKeyword(e.target.value)}
                        placeholder="输入申请编号/客户名称"
                      />
                      <Button variant="outline" onClick={() => queryClient.invalidateQueries({ queryKey: ['finance-financing-applications'] })}>刷新</Button>
                    </div>
                    {financingFiltered.length > 0 ? (
                      <>
                      <div className="md:hidden space-y-3 p-4">
                        {financingFiltered.slice(0, 10).map((item) => (
                          <MobileCard
                            key={item.id || item.applicationNo}
                            title={item.applicationNo || item.id}
                            badge={<span className="text-xs text-gray-500">{financingStatusMap[item.status] || item.status || '待审批'}</span>}
                            fields={[
                              { label: '申请金额', value: `¥${parseFloat(item.requestedAmount || item.applyAmount || 0).toFixed(2)}` },
                              { label: '申请时间', value: formatDateTime(item.createTime, 'YYYY-MM-DD HH:mm') },
                            ]}
                          />
                        ))}
                      </div>

                      <div className="hidden md:block overflow-x-auto">
                        <table className="table">
                          <thead>
                            <tr>
                              <th>申请编号</th>
                              <th>申请金额</th>
                              <th>状态</th>
                              <th>申请时间</th>
                            </tr>
                          </thead>
                          <tbody>
                            {financingFiltered.slice(0, 10).map((item) => (
                              <tr key={item.id || item.applicationNo}>
                                <td>{item.applicationNo || item.id}</td>
                                <td>¥{parseFloat(item.requestedAmount || item.applyAmount || 0).toFixed(2)}</td>
                                <td>{financingStatusMap[item.status] || item.status || '待审批'}</td>
                                <td>{formatDateTime(item.createTime, 'YYYY-MM-DD HH:mm')}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      </>
                    ) : (
                      <EmptyState title="暂无融资申请进度" description="点击上方“立即申请”发起融资" />
                    )}
                  </Card.Body>
                </Card>

                <div className="grid lg:grid-cols-2 gap-6">
                  <Card>
                    <Card.Header>
                      <h5 className="font-semibold">融资成功案例</h5>
                    </Card.Header>
                    <Card.Body>
                      <div className="space-y-3">
                        {financingCases.map((item) => (
                          <div key={item.company} className="p-4 rounded-lg border border-gray-100 bg-gray-50">
                            <p className="font-medium text-gray-900">{item.company}</p>
                            <p className="text-sm text-gray-600 mt-1">融资额度：¥{item.amount}</p>
                            <p className="text-xs text-gray-500 mt-1">结果：{item.result}</p>
                          </div>
                        ))}
                      </div>
                    </Card.Body>
                  </Card>

                  <Card>
                    <Card.Header>
                      <h5 className="font-semibold flex items-center gap-2"><FaCalculator className="text-primary-500" /> 融资计算器</h5>
                    </Card.Header>
                    <Card.Body>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <label className="form-label">本金</label>
                          <input className="form-control" type="number" value={calculatorPrincipal} onChange={(e) => setCalculatorPrincipal(e.target.value)} />
                        </div>
                        <div>
                          <label className="form-label">年利率(%)</label>
                          <input className="form-control" type="number" value={calculatorRate} onChange={(e) => setCalculatorRate(e.target.value)} />
                        </div>
                        <div>
                          <label className="form-label">期数(月)</label>
                          <input className="form-control" type="number" value={calculatorMonths} onChange={(e) => setCalculatorMonths(e.target.value)} />
                        </div>
                      </div>
                      {financingCalc && (
                        <div className="mt-4 p-4 rounded-lg bg-primary-50 border border-primary-100 text-sm">
                          <p>月供：<span className="font-semibold text-primary-700">¥{financingCalc.monthly.toFixed(2)}</span></p>
                          <p className="mt-1">总利息：¥{financingCalc.totalInterest.toFixed(2)}</p>
                          <p className="mt-1">还款总额：¥{financingCalc.totalRepayment.toFixed(2)}</p>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </div>
              </div>
            )}

            {activeTab === 'forex' && (
              <div className="space-y-6">
                <Card>
                  <Card.Header>
                    <h5 className="font-semibold flex items-center gap-2">
                      <FaExchangeAlt className="text-primary-500" /> 收汇服务
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <label className="form-label">收汇币种</label>
                        <select
                          className="form-control"
                          value={forexTargetCurrency}
                          onChange={(e) => setForexTargetCurrency(e.target.value)}
                        >
                          {forexTargets.map((code) => (
                            <option key={code} value={code}>{code}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="form-label">收汇金额</label>
                        <input
                          type="number"
                          className="form-control"
                          value={forexAmount}
                          onChange={(e) => setForexAmount(e.target.value)}
                          min="0"
                          step="0.01"
                          placeholder="请输入金额"
                        />
                      </div>
                      <div>
                        <label className="form-label">备注</label>
                        <input
                          type="text"
                          className="form-control"
                          value={forexRemark}
                          onChange={(e) => setForexRemark(e.target.value)}
                          placeholder="例如：结汇用途、订单号"
                        />
                      </div>
                    </div>
                    <div className="mt-4 flex items-center gap-3">
                      <Button loading={forexMutation.isPending} onClick={handleForexSubmit}>
                        提交收汇申请
                      </Button>
                      <span className="text-xs text-gray-500">提交后专员会联系确认账户信息与汇率</span>
                    </div>
                  </Card.Body>
                </Card>

                <Card>
                  <Card.Header>
                    <h5 className="font-semibold flex items-center gap-2"><FaFileAlt className="text-primary-500" /> 收汇申请记录</h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="grid lg:grid-cols-3 gap-6">
                      <div className="lg:col-span-1 space-y-3">
                        {forexAuditQueue.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => setActiveForexAuditId(item.id)}
                            className={`w-full text-left p-3 rounded-lg border transition-all ${
                              activeForexAuditId === item.id
                                ? 'border-primary bg-primary-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <p className="font-medium text-sm">{item.id}</p>
                            <p className="text-xs text-gray-500 mt-1">{item.currency} {item.amount}</p>
                            <p className="text-xs mt-1 text-primary-600">状态：{item.status}</p>
                          </button>
                        ))}
                      </div>

                      <div className="lg:col-span-2">
                        {activeForexAudit ? (
                          <div className="space-y-4">
                            <div className="grid grid-cols-fluid-md gap-4 text-sm">
                              <div className="p-3 rounded-lg bg-gray-50">
                                <p className="text-gray-500">收入来源证明</p>
                                <p className="font-medium mt-1">{activeForexAudit.sourceProof}</p>
                              </div>
                              <div className="p-3 rounded-lg bg-gray-50">
                                <p className="text-gray-500">交易背景资料</p>
                                <p className="font-medium mt-1">{activeForexAudit.tradeBackground}</p>
                              </div>
                              <div className="p-3 rounded-lg bg-gray-50 md:col-span-2">
                                <p className="text-gray-500">申请人资质文件</p>
                                <p className="font-medium mt-1">{activeForexAudit.applicantQualification}</p>
                              </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-3">
                              <Button variant="secondary" onClick={() => setActiveForexAuditId('')}>返回上一级</Button>
                            </div>

                            <div className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                              <p className="font-medium mb-2">流程信息</p>
                              <div className="space-y-2 text-sm">
                                {activeForexAudit.process.map((step) => (
                                  <div key={step.name} className="flex items-center justify-between">
                                    <span className={step.done ? 'text-gray-800' : 'text-gray-500'}>{step.name}</span>
                                    <span className="text-xs text-gray-500">{step.time}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <EmptyState title="请选择一条收汇申请" description="左侧选择后可查看申请资料与流程" />
                        )}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            )}

            {activeTab === 'exchange' && (
              <Card>
                <Card.Header>
                  <h5 className="font-semibold flex items-center gap-2">
                    <FaExchangeAlt className="text-primary-500" /> 汇率测算
                  </h5>
                </Card.Header>
                <Card.Body>
                  <div className="grid grid-cols-fluid-sm gap-4 items-end">
                    <div>
                      <label className="form-label">源币种</label>
                      <select className="form-control" value={sourceCurrency} onChange={(e) => setSourceCurrency(e.target.value)}>
                        {currencyOptions.map((code) => <option key={code} value={code}>{code}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="form-label">目标币种</label>
                      <select className="form-control" value={targetCurrency} onChange={(e) => setTargetCurrency(e.target.value)}>
                        {currencyOptions.map((code) => <option key={code} value={code}>{code}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="form-label">金额</label>
                      <input
                        type="number"
                        className="form-control"
                        value={exchangeAmount}
                        onChange={(e) => setExchangeAmount(e.target.value)}
                        min="0"
                        step="0.01"
                        placeholder="请输入金额"
                      />
                    </div>
                    <Button loading={exchangeMutation.isPending} onClick={handleCalculateExchange}>开始测算</Button>
                  </div>

                  {exchangeResult && (
                    <div className="mt-6 p-4 bg-primary-50 rounded-lg border border-primary-100">
                      <p className="text-sm text-gray-600 mb-1">当前汇率：1 {exchangeResult.source_currency} = {parseFloat(exchangeResult.exchange_rate || 0).toFixed(4)} {exchangeResult.target_currency}</p>
                      <p className="text-xl font-semibold text-primary-700">
                        {parseFloat(exchangeResult.amount || 0).toFixed(2)} {exchangeResult.source_currency}
                        {' ≈ '}
                        {parseFloat(exchangeResult.converted_amount || 0).toFixed(2)} {exchangeResult.target_currency}
                      </p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            )}

            {activeTab === 'reconciliation' && (
              <Card>
                <Card.Header>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h5 className="font-semibold flex items-center gap-2">
                      <FaCheckCircle className="text-primary-500" /> 对账管理
                    </h5>
                    <select
                      className="form-control w-44"
                      value={reconcileStatus}
                      onChange={(e) => setReconcileStatus(e.target.value)}
                    >
                      <option value="">全部状态</option>
                      <option value="unreconciled">未对账</option>
                      <option value="reconciled">已对账</option>
                      <option value="abnormal">异常</option>
                    </select>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  {reconciliationList.length > 0 ? (
                    <>
                    <div className="md:hidden space-y-3 p-4">
                      {reconciliationList.map((item) => (
                        <MobileCard
                          key={item.id}
                          title={item.transactionNo || item.id}
                          badge={<span className="text-xs text-gray-500">{typeLabelMap[item.transactionType] || item.transactionType}</span>}
                          fields={[
                            { label: '金额', value: `¥${parseFloat(item.amount || 0).toFixed(2)}` },
                            { label: '状态', value: reconcileLabelMap[item.reconcileStatus] || item.reconcileStatus || '未对账' },
                            { label: '结果', value: item.reconcileResult === 'exception' ? '异常' : '正常' },
                            { label: '时间', value: formatDateTime(item.createTime, 'YYYY-MM-DD HH:mm') },
                          ]}
                          actions={
                            <>
                              <Button
                                size="sm"
                                disabled={!canMarkReconciled(item)}
                                loading={markMutation.isPending}
                                onClick={() => markMutation.mutate(item.id)}
                              >
                                标记为已对账
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                icon={<FaExclamationTriangle />}
                                onClick={() => setExceptionModal({ visible: true, transactionId: item.id })}
                              >
                                异常
                              </Button>
                            </>
                          }
                        />
                      ))}
                    </div>

                    <div className="hidden md:block overflow-x-auto">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>交易编号</th>
                            <th>类型</th>
                            <th>金额</th>
                            <th>状态</th>
                            <th>结果</th>
                            <th>时间</th>
                            <th>操作</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reconciliationList.map((item) => (
                            <tr key={item.id}>
                              <td>{item.transactionNo || item.id}</td>
                              <td>{typeLabelMap[item.transactionType] || item.transactionType}</td>
                              <td>¥{parseFloat(item.amount || 0).toFixed(2)}</td>
                              <td>{reconcileLabelMap[item.reconcileStatus] || item.reconcileStatus || '未对账'}</td>
                              <td>{item.reconcileResult === 'exception' ? '异常' : '正常'}</td>
                              <td>{formatDateTime(item.createTime, 'YYYY-MM-DD HH:mm')}</td>
                              <td>
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    disabled={!canMarkReconciled(item)}
                                    loading={markMutation.isPending}
                                    onClick={() => markMutation.mutate(item.id)}
                                  >
                                    标记为已对账
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    icon={<FaExclamationTriangle />}
                                    onClick={() => setExceptionModal({ visible: true, transactionId: item.id })}
                                  >
                                    异常
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    </>
                  ) : (
                    <EmptyState title="暂无对账记录" description="当前没有待处理的对账数据" />
                  )}
                </Card.Body>
              </Card>
            )}

            {activeTab === 'transaction' && (
              <Card>
                <Card.Header>
                  <h5 className="font-semibold flex items-center gap-2">
                    <FaSearch className="text-primary-500" /> 交易查询
                  </h5>
                </Card.Header>
                <Card.Body>
                  <div className="grid md:grid-cols-3 gap-4 mb-4">
                    <input
                      type="text"
                      className="form-control"
                      value={transactionKeyword}
                      onChange={(e) => setTransactionKeyword(e.target.value)}
                      placeholder="按交易编号/说明查询"
                    />
                    <select
                      className="form-control"
                      value={transactionType}
                      onChange={(e) => setTransactionType(e.target.value)}
                    >
                      <option value="">全部类型</option>
                      <option value="recharge">充值</option>
                      <option value="consume">消费</option>
                      <option value="payment">付款</option>
                    </select>
                    <Button variant="outline" onClick={() => queryClient.invalidateQueries({ queryKey: ['finance-transactions'] })}>刷新</Button>
                  </div>

                  {transactions.length > 0 ? (
                    <>
                    {renderTxnCards(transactions)}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>交易编号</th>
                            <th>类型</th>
                            <th>金额</th>
                            <th>余额</th>
                            <th>说明</th>
                            <th>时间</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactions.map((item) => (
                            <tr key={item.id}>
                              <td>{item.transactionNo || item.id}</td>
                              <td>{typeLabelMap[item.transactionType] || item.transactionType}</td>
                              <td>¥{parseFloat(item.amount || 0).toFixed(2)}</td>
                              <td>¥{parseFloat(item.balanceAfter || 0).toFixed(2)}</td>
                              <td>{item.description || '-'}</td>
                              <td>{formatDateTime(item.createTime, 'YYYY-MM-DD HH:mm')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    </>
                  ) : (
                    <EmptyState title="未查询到交易记录" description="请调整筛选条件后重试" />
                  )}
                </Card.Body>
              </Card>
            )}

            {activeTab === 'payment' && (
              <Card>
                <Card.Header>
                  {/*
                    标题跟随入口名而不是写死「付款记录」：「付款」(?tab=pay) 与「付款记录」
                    (?tab=payment-records) 是同一个面板的两个入口，写死会出现「点了付款、
                    标题却是付款记录」，与上方高亮的 tab 和面包屑都对不上。
                    entryLabel 已按 URL 原值解析出这两个名字，直接用它，三处即保持一致。
                  */}
                  <h5 className="font-semibold flex items-center gap-2">
                    <FaArrowUp className="text-primary-500" /> {entryLabel}
                  </h5>
                </Card.Header>
                <Card.Body>
                  <div className="grid grid-cols-fluid-md gap-4 mb-4">
                    <input
                      type="text"
                      className="form-control"
                      value={paymentKeyword}
                      onChange={(e) => setPaymentKeyword(e.target.value)}
                      placeholder="按交易编号/说明查询付款记录"
                    />
                    <Button variant="outline" onClick={() => queryClient.invalidateQueries({ queryKey: ['finance-payments'] })}>刷新</Button>
                  </div>

                  {payments.length > 0 ? (
                    <>
                    {renderTxnCards(payments)}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>交易编号</th>
                            <th>类型</th>
                            <th>金额</th>
                            <th>余额</th>
                            <th>说明</th>
                            <th>时间</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payments.map((item) => (
                            <tr key={item.id}>
                              <td>{item.transactionNo || item.id}</td>
                              <td>{typeLabelMap[item.transactionType] || item.transactionType}</td>
                              <td className="text-red-600">¥{parseFloat(item.amount || 0).toFixed(2)}</td>
                              <td>¥{parseFloat(item.balanceAfter || 0).toFixed(2)}</td>
                              <td>{item.description || '-'}</td>
                              <td>{formatDateTime(item.createTime, 'YYYY-MM-DD HH:mm')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    </>
                  ) : (
                    <EmptyState title="暂无付款记录" description="当前没有付款类交易" />
                  )}
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </div>

      <Modal
        open={financingApplyVisible}
        onClose={() => setFinancingApplyVisible(false)}
        size="xl"
        title={
          <span>
            融资申请
            <span className="block text-sm font-normal text-gray-500 mt-1">请填写基本融资申请信息，提交后由专员联系您</span>
          </span>
        }
      >
            <div className="space-y-4">
              <div className="grid grid-cols-fluid-md gap-4">
                <div>
                  <label className="form-label">融资产品</label>
                  <select className="form-control" value={financingType} onChange={(e) => setFinancingType(e.target.value)}>
                    <option value="">请选择产品</option>
                    {financingTypes.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">申请金额 (CNY)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={financingAmount}
                    onChange={(e) => setFinancingAmount(e.target.value)}
                    placeholder="请输入申请金额"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="form-label">融资期限(月)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={financingTerm}
                    onChange={(e) => setFinancingTerm(e.target.value)}
                    placeholder="例如 6"
                  />
                </div>
                <div>
                  <label className="form-label">联系人姓名</label>
                  <input
                    type="text"
                    className="form-control"
                    value={financingContactName}
                    onChange={(e) => setFinancingContactName(e.target.value)}
                    placeholder="请输入联系人"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="form-label">联系电话</label>
                  <input
                    type="text"
                    className="form-control"
                    value={financingContactPhone}
                    onChange={(e) => setFinancingContactPhone(e.target.value)}
                    placeholder="请输入联系电话"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="form-label">用途说明</label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={financingRemark}
                    onChange={(e) => setFinancingRemark(e.target.value)}
                    placeholder="补充订单号、用途等信息"
                  />
                </div>
              </div>
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
                <Button variant="secondary" onClick={() => setFinancingApplyVisible(false)} className="w-full sm:w-auto">取消</Button>
                <Button loading={financingMutation.isPending} onClick={handleFinancingSubmit} className="w-full sm:w-auto">提交申请</Button>
              </div>
            </div>
      </Modal>

      <Modal
        open={exceptionModal.visible}
        onClose={() => setExceptionModal({ visible: false, transactionId: null })}
        size="lg"
        title={
          <span>
            提交对账异常
            <span className="block text-sm font-normal text-gray-500 mt-1">请填写对账问题，后台会收到，问题查询与异常警报将同步更新。</span>
          </span>
        }
      >
            <div>
              <textarea
                className="form-control"
                rows={5}
                placeholder="请填写异常说明..."
                value={exceptionText}
                onChange={(e) => setExceptionText(e.target.value)}
              />
              <div className="mt-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
                <Button variant="secondary" onClick={() => setExceptionModal({ visible: false, transactionId: null })} className="w-full sm:w-auto">取消</Button>
                <Button
                  variant="danger"
                  loading={exceptionMutation.isPending}
                  className="w-full sm:w-auto"
                  onClick={() => {
                    if (!exceptionText.trim()) {
                      toast.error('请填写异常说明')
                      return
                    }
                    exceptionMutation.mutate({
                      transactionId: exceptionModal.transactionId,
                      issueDescription: exceptionText.trim(),
                    })
                  }}
                >
                  提交异常
                </Button>
              </div>
            </div>
      </Modal>
    </div>
  )
}
